import { Checkbox, CheckboxGroup, TextOrHTML } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import type React from "react";
import { isShadowQuestion } from "../../../../../../../features/questionnaire";
import { containsHtmlMarkup, snakeCase } from "../../../../../../../utils";
import type { QuestionComponentProps } from "../../types";

const makeLabel = (label: string) => {
  if (containsHtmlMarkup(label)) {
    return <TextOrHTML text={label} />;
  }
  return label;
};

export const triggerShadowQuestionAnswerEvent = ({
  previousValue,
  newValues,
  snowplow,
  sectionTitle,
}: {
  previousValue: string[];
  newValues: string[];
  snowplow: ReturnType<typeof useSnowplowContext>["snowplow"];
  sectionTitle: string | undefined;
}) => {
  const previousValues = new Set(previousValue);
  const newValuesSet = new Set(newValues);

  const added = newValues.filter(v => !previousValues.has(v));
  const removed = previousValue.filter(v => !newValuesSet.has(v));

  const changedItem = added[0] || removed[0];

  snowplow?.trigger("questionAnswered", {
    section: sectionTitle ?? "",
    question: snakeCase(changedItem),
    answer: added[0] ? "yes" : "no",
  });
};

export const MultiSelectQuestion = ({
  options,
  config,
  sectionTitle,
  questionFieldProps,
}: QuestionComponentProps) => {
  const { value, onChange, questionLabelProps, helpInfo, ...otherProps } =
    questionFieldProps;
  const lastItemDisables = !!config?.lastItemDisables;
  const consolidatedDefaultNoAnswer = (
    config as Record<string, unknown> | undefined
  )?.consolidatedDefaultNoAnswer as string | undefined;

  const { snowplow } = useSnowplowContext();

  if (!options?.length) {
    console.warn(`No required options found`);
    return null;
  }

  // Strip sentinel values that represent "no selection" from the backend:
  // - "" (empty string sentinel for questions without consolidatedDefaultNoAnswer)
  // - consolidatedDefaultNoAnswer (e.g. "None of the above") when it arrives via state
  //   while the question is visible — it should not pre-populate the active selection
  const sentinels = new Set<string>([""]);
  if (consolidatedDefaultNoAnswer) sentinels.add(consolidatedDefaultNoAnswer);
  const safeValue = Array.isArray(value)
    ? value.filter(v => !sentinels.has(v))
    : [];

  const handleGroupChange = (values: string[], shouldUpdate?: boolean) => {
    // Strip sentinels from values produced by CheckboxGroup's internal state
    // (e.g. when "None of the above" was pre-selected from a stale server answer
    // and the user checks a real option, producing ["None of the above", "real option"])
    const cleanValues = values.filter(v => !sentinels.has(v));

    if (isShadowQuestion(questionFieldProps.name)) {
      triggerShadowQuestionAnswerEvent({
        previousValue: safeValue,
        newValues: cleanValues,
        snowplow,
        sectionTitle,
      });
    }

    onChange!({
      target: {
        name: questionFieldProps.name,
        value: cleanValues,
        shouldUpdate,
      },
    });
  };

  const handleOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    optionValue: string,
  ) => {
    const isChecked = event.target.checked;

    // If lastItemDisables=true and the last option is checked,
    // we want to uncheck all other options and only keep the last option checked
    const isLastOption = optionValue === options[options.length - 1].value;
    if (lastItemDisables && isChecked && isLastOption) {
      handleGroupChange([optionValue], true);
      return;
    }

    const currentValues = new Set(safeValue);
    if (isChecked) {
      currentValues.add(optionValue);
    } else {
      currentValues.delete(optionValue);
    }

    handleGroupChange(Array.from(currentValues), true);
  };

  return (
    <CheckboxGroup
      {...otherProps}
      lastItemDisables={lastItemDisables}
      defaultValue={safeValue}
      onChange={handleGroupChange}
    >
      {options.map(option => (
        <Checkbox
          key={option.value}
          label={makeLabel(option.label)}
          value={option.value}
          onChange={
            option.shouldUpdate
              ? e => handleOptionChange(e, option.value)
              : undefined
          }
        />
      ))}
    </CheckboxGroup>
  );
};
